import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Eraser, Keyboard, PenTool } from 'lucide-react';

interface SignaturePadProps {
  value?: string;
  onChange?: (val: string) => void;
  onSave?: (val: string) => void;
  placeholder?: string;
  label?: string;
}

export default function SignaturePad({
  value = '',
  onChange,
  onSave,
  placeholder,
  label
}: SignaturePadProps) {
  const [mode, setMode] = useState<'type' | 'draw'>('type');
  const [typedName, setTypedName] = useState(value && !value.startsWith('data:image') && !value.startsWith('TYPED:') ? value : value.startsWith('TYPED:') ? value.slice(6) : '');
  const sigCanvas = useRef<SignatureCanvas>(null);

  const emitChange = (val: string) => {
    if (onChange) onChange(val);
    if (onSave) onSave(val);
  };

  const handleModeChange = (newMode: 'type' | 'draw') => {
    setMode(newMode);
    emitChange('');
    if (newMode === 'draw' && sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const handleClear = () => {
    if (mode === 'draw' && sigCanvas.current) {
      sigCanvas.current.clear();
    }
    setTypedName('');
    emitChange('');
  };

  const handleDrawEnd = () => {
    if (sigCanvas.current) {
      if (sigCanvas.current.isEmpty()) {
        emitChange('');
      } else {
        emitChange(sigCanvas.current.toDataURL('image/png'));
      }
    }
  };

  const handleTypeChange = (name: string) => {
    setTypedName(name);
    emitChange(name.trim() ? `TYPED:${name.trim()}` : '');
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <div className="flex items-center justify-between bg-slate-50 border-b border-slate-200 px-3 py-2">
        <div className="flex gap-1.5">
          <Button
            type="button"
            variant={mode === 'type' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 text-xs font-semibold"
            onClick={() => handleModeChange('type')}
          >
            <Keyboard className="w-3.5 h-3.5 mr-1" />
            Type
          </Button>
          <Button
            type="button"
            variant={mode === 'draw' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 text-xs font-semibold"
            onClick={() => handleModeChange('draw')}
          >
            <PenTool className="w-3.5 h-3.5 mr-1" />
            Draw
          </Button>
        </div>
        {mode === 'draw' && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-slate-500 hover:text-slate-900"
            onClick={handleClear}
          >
            <Eraser className="w-3.5 h-3.5 mr-1" />
            Clear
          </Button>
        )}
      </div>

      <div className="p-3">
        {mode === 'type' ? (
          <Input
            value={typedName}
            onChange={(e) => handleTypeChange(e.target.value)}
            placeholder={placeholder || "Type full legal name to sign..."}
            className="font-serif italic text-2xl h-16 border-none shadow-none focus-visible:ring-0 bg-transparent px-2"
          />
        ) : (
          <div className="h-28 bg-slate-50/50 rounded-lg border border-dashed border-slate-200 relative">
            <SignatureCanvas
              ref={sigCanvas}
              onEnd={handleDrawEnd}
              canvasProps={{
                className: 'w-full h-full absolute top-0 left-0'
              }}
            />
            {!value && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-slate-400 text-xs">
                Draw your signature here with your finger or mouse
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export { SignaturePad };
